import { getParsedBackground } from './background/parseBackground'
import { getParsedBoxShadow } from './box-shadow/parseBoxShadow'
import { getParsedAnimation } from './parseAnimation'
import { getParsedBorder } from './parseBorder'
import { getParsedBorderRadius } from './parseBorderRadius'
import { getParsedFlex } from './parseFlex'
import { getParsedFont } from './parseFont'
import { getParsedGap } from './parseGap'
import { getParsedOutline } from './parseOutline'
import { getParsedOverflow } from './parseOverflow'
import { getParsedTextDecoration } from './parseTextDecoration'
import { getParsedTransition } from './parseTransition'
import { getValue, parse, parseMultipleValues } from './shared'
import { getParsedTextShadow } from './text-shadow/parseTextShadow'
import { parsePerspective } from './transforms/parsePerspective'
import { getParsedRotate } from './transforms/parseRotate'
import { parseScale } from './transforms/parseScale'
import { parseTranslate } from './transforms/parseTranslate'
import type {
  ParsedMatrix,
  ParsedStyle,
  ParsedTransformPerspective,
  ParsedTransformRotate,
  ParsedTransformScale,
  ParsedTransformSkew,
  ParsedTransformTranslate,
  ParsedValueType,
  ThemeVariables,
} from './types'

export const parseCss = (args: {
  variables?: ThemeVariables
  style: Record<string, any>
}) => {
  const variables = args.variables
    ? Object.values(args.variables).flatMap((v) => v)
    : []

  const parseTransformAll = (transform: ParsedValueType[]) => {
    if (!transform) {
      return null
    }
    const allTransforms: Array<
      | ParsedTransformRotate
      | ParsedTransformScale
      | ParsedTransformTranslate
      | ParsedTransformSkew
      | ParsedTransformRotate
      | ParsedTransformPerspective
      | ParsedMatrix
    > = []

    // Get the rotate from the transform property
    const rotates = transform.filter(
      (t) => t.type === 'functionArguments' && t.name.includes('rotate'),
    )

    rotates.map((r) => {
      if (r.type === 'functionArguments') {
        switch (r.name) {
          case 'rotate3d':
            allTransforms.push({
              [r.name]: {
                x: r.arguments[0],
                y: r.arguments[1],
                z: r.arguments[2],
                angle: r.arguments[3],
              },
            })
            break
          case 'rotateX':
            allTransforms.push({
              [r.name]: {
                angle: r.arguments[0],
              },
            })
            break
          case 'rotateY':
            allTransforms.push({
              [r.name]: {
                angle: r.arguments[0],
              },
            })
            break
          case 'rotateZ':
            allTransforms.push({
              [r.name]: {
                angle: r.arguments[0],
              },
            })
            break
          case 'rotate':
            allTransforms.push({
              [r.name]: {
                angle: r.arguments[0],
              },
            })
            break
        }
      }
    })

    // Get the translate from the transform property
    const translates = transform.filter(
      (t) => t.type === 'functionArguments' && t.name.includes('translate'),
    )

    translates.map((t) => {
      if (t.type === 'functionArguments') {
        switch (t.name) {
          case 'translate3d':
            allTransforms.push({
              [t.name]: {
                x: t.arguments[0],
                y: t.arguments[1],
                z: t.arguments[2],
              },
            })
            break
          case 'translateX':
            allTransforms.push({
              [t.name]: {
                x: t.arguments[0],
              },
            })
            break
          case 'translateY':
            allTransforms.push({
              [t.name]: {
                y: t.arguments[0],
              },
            })
            break
          case 'translateZ':
            allTransforms.push({
              [t.name]: {
                z: t.arguments[0],
              },
            })
            break
          case 'translate':
            allTransforms.push({
              [t.name]: {
                x: t.arguments[0],
                y: t.arguments[1],
              },
            })
            break
        }
      }
    })

    // Get the scale from the transform property
    const scales = transform.filter(
      (t) => t.type === 'functionArguments' && t.name.includes('scale'),
    )

    scales.map((scale) => {
      if (scale.type === 'functionArguments') {
        switch (scale.name) {
          case 'scale3d':
            allTransforms.push({
              [scale.name]: {
                x: scale.arguments[0],
                y: scale.arguments[1],
                z: scale.arguments[2],
              },
            })
            break
          case 'scaleX':
            allTransforms.push({
              [scale.name]: {
                x: scale.arguments[0],
              },
            })
            break
          case 'scaleY':
            allTransforms.push({
              [scale.name]: {
                y: scale.arguments[0],
              },
            })
            break
          case 'scaleZ':
            allTransforms.push({
              [scale.name]: {
                z: scale.arguments[0],
              },
            })
            break
          case 'scale':
            allTransforms.push({
              [scale.name]: {
                x: scale.arguments[0],
                y: scale.arguments[1] ?? scale.arguments[0],
              },
            })
            break
        }
      }
    })

    // Get the skew from the transform property
    const skews = transform.filter(
      (t) => t.type === 'functionArguments' && t.name.includes('skew'),
    )

    skews.map((skew) => {
      if (skew.type === 'functionArguments') {
        switch (skew.name) {
          case 'skew':
            allTransforms.push({
              [skew.name]: {
                x: skew.arguments[0],
                y: skew.arguments[1] ?? skew.arguments[0],
              },
            })
            break
          case 'skewX':
            allTransforms.push({
              [skew.name]: {
                x: skew.arguments[0],
              },
            })
            break
          case 'skewY':
            allTransforms.push({
              [skew.name]: {
                y: skew.arguments[0],
              },
            })
            break
        }
      }
    })

    // Get the matrix from the transform property
    const matrixs = transform.filter(
      (t) => t.type === 'functionArguments' && t.name.includes('matrix'),
    )

    matrixs.map((matrix) => {
      if (matrix.type === 'functionArguments') {
        switch (matrix.name) {
          case 'matrix3d':
            allTransforms.push({
              [matrix.name]: {
                a1: matrix.arguments[0],
                b1: matrix.arguments[1],
                c1: matrix.arguments[2],
                d1: matrix.arguments[3],
                a2: matrix.arguments[4],
                b2: matrix.arguments[5],
                c2: matrix.arguments[6],
                d2: matrix.arguments[7],
                a3: matrix.arguments[8],
                b3: matrix.arguments[9],
                c3: matrix.arguments[10],
                d3: matrix.arguments[11],
                a4: matrix.arguments[12],
                b4: matrix.arguments[13],
                c4: matrix.arguments[14],
                d4: matrix.arguments[15],
              },
            })
            break
          case 'matrix':
            allTransforms.push({
              [matrix.name]: {
                a: matrix.arguments[0],
                b: matrix.arguments[1],
                c: matrix.arguments[2],
                d: matrix.arguments[3],
                x: matrix.arguments[4],
                y: matrix.arguments[5],
              },
            })
            break
        }
      }
    })

    // Get the perspective from the transform property
    const perspectives = transform.filter(
      (t) => t.type === 'functionArguments' && t.name.includes('perspective'),
    )

    perspectives.map((perspective) => {
      if (
        perspective.type === 'functionArguments' &&
        perspective.name === 'perspective'
      ) {
        allTransforms.push({
          [perspective.name]: { distance: perspective.arguments[0] },
        })
      }
    })

    return allTransforms
  }

  if (args.style.constructor !== Object) {
    return console.error('The value you provided is not valid!')
  }

  let cssText = ''
  Object.entries(args.style).map(([key, value]) => {
    cssText += `${key}: ${value}; `
  })

  // We want to sort the style so the properties with the value !important comes at the end.
  const sortedStyleArray = Object.entries(args.style).toSorted((a, b) => {
    if (a[1].toString().includes('!important')) {
      return 1
    }
    if (b[1].toString().includes('!important')) {
      return -1
    } else {
      return 0
    }
  })

  const sortedStyle = Object.fromEntries(sortedStyleArray)

  const element = document.createElement('div')
  element.style = cssText

  const style: CSSStyleDeclaration = element.style

  const parsedStyle: ParsedStyle = {}

  // Backround
  parsedStyle.background = getParsedBackground(sortedStyle, variables)

  // Transition
  parsedStyle.transition = getParsedTransition(sortedStyle, variables)

  // Animation
  parsedStyle.animation = getParsedAnimation(sortedStyle, variables)

  // Box shadow
  parsedStyle.boxShadow = getParsedBoxShadow(sortedStyle, variables)

  // Text shadow
  parsedStyle.textShadow = getParsedTextShadow(sortedStyle, variables)

  // Margin
  if (
    style.marginTop !== '' ||
    style.marginRight !== '' ||
    style.marginBottom !== '' ||
    style.marginLeft !== ''
  ) {
    const marginTop = parse({ input: style.marginTop })
    const marginRight = parse({ input: style.marginRight })
    const marginBottom = parse({ input: style.marginBottom })
    const marginLeft = parse({ input: style.marginLeft })

    parsedStyle.margin = {
      left: parseMultipleValues(getValue(marginLeft[0]))[0] ?? null,
      right: parseMultipleValues(getValue(marginRight[0]))[0] ?? null,
      top: parseMultipleValues(getValue(marginTop[0]))[0] ?? null,
      bottom: parseMultipleValues(getValue(marginBottom[0]))[0] ?? null,
    }
  } else {
    if (style.margin !== '') {
      const margin = parse({ input: style.margin })
      const marginValues = parseMultipleValues(getValue(margin[0]))

      parsedStyle.margin = {
        top: marginValues[0] ?? null,
        right: marginValues[1] ?? marginValues[0] ?? null,
        bottom: marginValues[2] ?? marginValues[0] ?? null,
        left: marginValues[3] ?? marginValues[1] ?? marginValues[0] ?? null,
      }
    } else {
      parsedStyle.margin = null
    }
  }

  // Padding
  if (
    style.paddingTop !== '' ||
    style.paddingRight !== '' ||
    style.paddingBottom !== '' ||
    style.paddingLeft !== ''
  ) {
    const paddingTop = parse({ input: style.paddingTop })
    const paddingRight = parse({ input: style.paddingRight })
    const paddingBottom = parse({ input: style.paddingBottom })
    const paddingLeft = parse({ input: style.paddingLeft })

    parsedStyle.padding = {
      left: parseMultipleValues(getValue(paddingLeft[0]))[0] ?? null,
      right: parseMultipleValues(getValue(paddingRight[0]))[0] ?? null,
      top: parseMultipleValues(getValue(paddingTop[0]))[0] ?? null,
      bottom: parseMultipleValues(getValue(paddingBottom[0]))[0] ?? null,
    }
  } else {
    if (style.padding !== '') {
      const padding = parse({ input: style.padding })
      const paddingValues = parseMultipleValues(getValue(padding[0]))

      parsedStyle.padding = {
        top: paddingValues[0] ?? null,
        right: paddingValues[1] ?? paddingValues[0] ?? null,
        bottom: paddingValues[2] ?? paddingValues[0] ?? null,
        left: paddingValues[3] ?? paddingValues[1] ?? paddingValues[0] ?? null,
      }
    } else {
      parsedStyle.padding = null
    }
  }

  // Translate
  parsedStyle.translate = parseTranslate(sortedStyle)

  // Rotate
  parsedStyle.rotate = getParsedRotate(sortedStyle, variables)

  // Scale
  parsedStyle.scale = parseScale(sortedStyle)

  // Perspective
  parsedStyle.perspective = parsePerspective(sortedStyle)

  // Transform
  if (
    style.transform !== '' ||
    style.transformOrigin !== '' ||
    style.transformStyle !== '' ||
    style.transformBox !== ''
  ) {
    const transform = parse({ input: style.transform })
    const transformOrigin = parse({ input: style.transformOrigin })
    const transformStyle = parse({ input: style.transformStyle })
    const transformBox = parse({ input: style.transformBox })

    parsedStyle.transform = { all: null, transformOrigin: null }

    parsedStyle.transform.all =
      parseTransformAll(parseMultipleValues(getValue(transform[0], true))) ?? []
    parsedStyle.transform.transformOrigin = {
      x: parseMultipleValues(getValue(transformOrigin[0]))[0] ?? null,
      y: parseMultipleValues(getValue(transformOrigin[0]))[1] ?? null,
      z: parseMultipleValues(getValue(transformOrigin[0]))[2] ?? null,
    }
    parsedStyle.transform.transformStyle =
      parseMultipleValues(getValue(transformStyle[0]))[0] ?? null
    parsedStyle.transform.transformBox =
      parseMultipleValues(getValue(transformBox[0]))[0] ?? null
  } else {
    parsedStyle.transform = null
  }

  // Inset
  if (
    sortedStyle.top ||
    sortedStyle.bottom ||
    sortedStyle.left ||
    sortedStyle.right
  ) {
    const top = parse({ input: sortedStyle.top })
    const bottom = parse({ input: sortedStyle.bottom })
    const left = parse({ input: sortedStyle.left })
    const right = parse({ input: sortedStyle.right })

    parsedStyle.inset = {
      top: top ? (parseMultipleValues(getValue(top[0]))[0] ?? null) : null,
      bottom: bottom
        ? (parseMultipleValues(getValue(bottom[0]))[0] ?? null)
        : null,
      left: left ? (parseMultipleValues(getValue(left[0]))[0] ?? null) : null,
      right: right
        ? (parseMultipleValues(getValue(right[0]))[0] ?? null)
        : null,
    }
  } else {
    if (sortedStyle.inset) {
      const inset = parse({ input: sortedStyle.inset })
      const insetValues = parseMultipleValues(getValue(inset?.[0]))

      parsedStyle.inset = {
        top: insetValues[0] ?? null,
        right: insetValues[1] ?? insetValues[0] ?? null,
        bottom: insetValues[2] ?? insetValues[0] ?? null,
        left: insetValues[3] ?? insetValues[1] ?? insetValues[0] ?? null,
      }
    } else {
      parsedStyle.inset = null
    }
  }

  // Overflow
  parsedStyle.overflow = getParsedOverflow(sortedStyle)

  // Outline
  parsedStyle.outline = getParsedOutline(sortedStyle, variables)

  // Border
  parsedStyle.border = getParsedBorder(sortedStyle, variables)

  // Border radius
  parsedStyle.borderRadius = getParsedBorderRadius(sortedStyle, variables)

  // Font
  parsedStyle.font = getParsedFont(sortedStyle, variables)

  // Flex
  parsedStyle.flex = getParsedFlex(sortedStyle, variables)

  // Gap
  parsedStyle.gap = getParsedGap(sortedStyle)

  // Text decoration
  parsedStyle.textDecoration = getParsedTextDecoration(sortedStyle, variables)

  if (sortedStyle['color']) {
    const color = parse({ input: sortedStyle['color'] })
    parsedStyle.color = parseMultipleValues(getValue(color[0]))[0] ?? null
  } else {
    parsedStyle.color = null
  }

  return parsedStyle
}
